// THE STALE-POLL CONTRACT (UNIFIED-FUNCTION F5.3). All eleven polls run through this hook.
//
// THE LAW: numbers are NEVER blanked because a refresh failed. Stale-and-labelled beats empty.
// An operator who sees "3 pending" that is four minutes old can still act; an operator who sees
// a spinner where a number was cannot, and does not know whether the number changed or the
// server died. So the hook keeps the last good value, records WHEN it was good, and hands the
// masthead a sentence to say about it.
//
// It also enforces the stamping law's other half (F5.4): a poll NEVER stamps a visit table.
// `usePoll` has no notion of stamping and cannot be given one — the read-then-stamp call belongs
// to the initial load, once, with `?stamp=1`.

import { useCallback, useEffect, useRef, useState } from 'react';

import { useT } from '../i18n/index.js';
import './usePoll.de.js';
import './usePoll.fr.js';

/** The default cadence. Three consecutive misses is what "stale" means, everywhere. */
export const POLL_MS = 30_000;
export const STALE_AFTER = 3;

export interface PollState<T> {
  /** The last GOOD value. Never cleared by a failure — that is the whole point. */
  data: T | null;
  /** True only before the first successful load. A refresh in flight is not "loading". */
  loading: boolean;
  /** The most recent failure's message, verbatim (B7). Null once a poll succeeds again. */
  error: string | null;
  /** When `data` was last true, as epoch ms. Null until the first success. */
  lastOkAt: number | null;
  /** `lastOkAt` is older than STALE_AFTER × interval — the masthead says so. */
  stale: boolean;
  /**
   * What the masthead prints. Null when everything is current — a healthy poll is silent.
   * Stale: `data updated 14:32 · reconnecting`.
   */
  notice: string | null;
  /** Force a refresh now (a mutation just landed, or the operator asked). */
  refresh: () => Promise<void>;
}

const hhmm = (ms: number): string => {
  const d = new Date(ms);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

/**
 * Poll `fn` every `intervalMs`.
 *
 * `fn` must be stable (wrap it in `useCallback`) — its identity is the dependency that restarts
 * the poll, exactly as with `useEffect`, so an inline arrow would restart it every render.
 */
export function usePoll<T>(fn: () => Promise<T>, intervalMs: number = POLL_MS): PollState<T> {
  const t = useT();
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastOkAt, setLastOkAt] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());
  // Survives unmount-during-flight: a resolved fetch must not setState on a dead component.
  const alive = useRef(true);

  const run = useCallback(async () => {
    try {
      const next = await fn();
      if (!alive.current) return;
      setData(next);
      setError(null);
      setLastOkAt(Date.now());
    } catch (err) {
      if (!alive.current) return;
      // The value stays. Only the error line changes.
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      if (alive.current) setLoading(false);
    }
  }, [fn]);

  useEffect(() => {
    alive.current = true;
    void run();
    const poll = setInterval(() => { void run(); }, intervalMs);
    // A second, cheap timer so `stale` becomes true on time even while every fetch is failing
    // (a failed poll never updates lastOkAt, so nothing else would re-render this).
    const tick = setInterval(() => setNow(Date.now()), 1_000);
    return () => {
      alive.current = false;
      clearInterval(poll);
      clearInterval(tick);
    };
  }, [run, intervalMs]);

  const stale = lastOkAt !== null && now - lastOkAt > STALE_AFTER * intervalMs;

  const notice =
    lastOkAt === null
      ? error
        // Nothing has ever loaded and the fetch is failing: there is no stale number to label,
        // so the error IS the state. The zone renders its `error` empty state.
        ? error
        : null
      : stale
        ? t('data updated {time} · reconnecting', { time: hhmm(lastOkAt) })
        : null;

  return { data, loading, error, lastOkAt, stale, notice, refresh: run };
}
